/**
 * 作业效能分析看板 — POST /api/produce/kanban/work-efficiency
 * Mock 兜底：VUE_APP_KANBAN_WORK_EFFICIENCY_USE_MOCK=true
 */
import request from '@/router/axios'

const API = '/api/produce/kanban/work-efficiency'
const USE_MOCK = process.env.VUE_APP_KANBAN_WORK_EFFICIENCY_USE_MOCK === 'true'

function unwrap(res) {
  const body = res && res.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function toNumber(value) {
  const n = Number(value)
  return isFinite(n) ? n : 0
}

function toNumberArray(value) {
  return Array.isArray(value) ? value.map(toNumber) : []
}

function toStringArray(value) {
  return Array.isArray(value) ? value.map((item) => (item == null ? '' : String(item))) : []
}

function normalizeKpiSummary(value = {}) {
  return {
    capacityRate: toNumber(value.capacityRate),
    capacityCompareText: value.capacityCompareText == null ? '' : String(value.capacityCompareText),
    avgDurationMinutes: toNumber(value.avgDurationMinutes),
    utilizationRate: toNumber(value.utilizationRate),
    runningHours: toNumber(value.runningHours),
    totalHours: toNumber(value.totalHours),
    faultHours: toNumber(value.faultHours)
  }
}

function normalizeWorkEfficiency(data = {}) {
  const dumperData = data.dumperData || {}
  const shiftData = data.shiftData || {}
  const deviceStatus = data.deviceStatus || {}
  const durationTrend = data.durationTrend || {}
  const faultTypes = data.faultTypes || {}

  return {
    kpiSummary: data.kpiSummary ? normalizeKpiSummary(data.kpiSummary) : null,
    dumperData: {
      dates: toStringArray(dumperData.dates),
      actualCapacity: toNumberArray(dumperData.actualCapacity),
      theoreticalCapacity: toNumberArray(dumperData.theoreticalCapacity)
    },
    shiftData: {
      shifts: toStringArray(shiftData.shifts),
      efficiencies: toNumberArray(shiftData.efficiencies),
      targets: toNumberArray(shiftData.targets)
    },
    deviceStatus: {
      running: toNumber(deviceStatus.running),
      standby: toNumber(deviceStatus.standby),
      fault: toNumber(deviceStatus.fault)
    },
    durationTrend: {
      dates: toStringArray(durationTrend.dates),
      dumperDuration: toNumberArray(durationTrend.dumperDuration)
    },
    faultTypes: {
      types: toStringArray(faultTypes.types),
      counts: toNumberArray(faultTypes.counts)
    }
  }
}

function mockData(period = 'week') {
  const week = {
    kpiSummary: {
      capacityRate: 102.7,
      capacityCompareText: '较上周提升 3.2%',
      avgDurationMinutes: 2.5,
      utilizationRate: 75.6,
      runningHours: 128.5,
      totalHours: 170,
      faultHours: 9.3
    },
    dumperData: {
      actualCapacity: [145, 152, 148, 160, 155, 162, 158],
      theoreticalCapacity: [150, 150, 150, 150, 150, 150, 150],
      dates: ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
    },
    shiftData: {
      shifts: ['早班 (06:00-14:00)', '中班 (14:00-22:00)', '晚班 (22:00-06:00)'],
      efficiencies: [92, 88, 76],
      targets: [85, 85, 85]
    },
    deviceStatus: { running: 128.5, standby: 32.2, fault: 9.3 },
    durationTrend: {
      dates: ['12/10', '12/11', '12/12', '12/13', '12/14', '12/15', '12/16'],
      dumperDuration: [2.8, 2.6, 2.9, 2.5, 2.4, 2.3, 2.2]
    },
    faultTypes: {
      types: ['机械故障', '电气故障', '液压系统', '传感器故障', '控制系统'],
      counts: [8, 12, 5, 7, 3]
    }
  }
  if (period === 'today') {
    return {
      kpiSummary: {
        capacityRate: 105.3,
        capacityCompareText: '较昨日提升 2.1%',
        avgDurationMinutes: 2.2,
        utilizationRate: 77.1,
        runningHours: 18.5,
        totalHours: 24,
        faultHours: 1.3
      },
      dumperData: { actualCapacity: [158], theoreticalCapacity: [150], dates: ['今日'] },
      shiftData: { shifts: week.shiftData.shifts, efficiencies: [94, 90, 78], targets: [85, 85, 85] },
      deviceStatus: { running: 18.5, standby: 4.2, fault: 1.3 },
      durationTrend: { dates: ['今日'], dumperDuration: [2.2] },
      faultTypes: week.faultTypes
    }
  }
  if (period === 'month') {
    return {
      kpiSummary: {
        capacityRate: 100.8,
        capacityCompareText: '较上月提升 1.5%',
        avgDurationMinutes: 2.4,
        utilizationRate: 75.8,
        runningHours: 520,
        totalHours: 686,
        faultHours: 38
      },
      dumperData: {
        actualCapacity: [148, 152, 150, 155],
        theoreticalCapacity: [150, 150, 150, 150],
        dates: ['第1周', '第2周', '第3周', '第4周']
      },
      shiftData: { shifts: week.shiftData.shifts, efficiencies: [89, 86, 74], targets: [85, 85, 85] },
      deviceStatus: { running: 520, standby: 128, fault: 38 },
      durationTrend: { dates: ['第1周', '第2周', '第3周', '第4周'], dumperDuration: [2.6, 2.5, 2.4, 2.3] },
      faultTypes: { types: week.faultTypes.types, counts: [32, 45, 21, 28, 12] }
    }
  }
  return week
}

export function fetchWorkEfficiency(params = {}) {
  if (USE_MOCK) return Promise.resolve(normalizeWorkEfficiency(mockData(params.period)))
  return request({ url: API, method: 'post', data: params }).then((res) => normalizeWorkEfficiency(unwrap(res)))
}
